import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { productsAPI } from '../../services/api';
import LanguageSwitcher from '../LanguageSwitcher';
import { 
  HiOutlineShoppingBag, 
  HiOutlineHeart, 
  HiOutlineUser, 
  HiOutlineSearch,
  HiOutlineMenu,
  HiOutlineX
} from 'react-icons/hi';

const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount: cartCount } = useCart();
  const { itemCount: wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();

  const handleCategoryClick = (slug) => {
    console.log('Navigating to category:', slug);
    window.location.href = `/shop?category=${slug}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
    setIsUserMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.length >= 2) {
        try {
          const response = await productsAPI.search(searchQuery);
          setSearchResults(response.data.products);
        } catch (error) {
          console.error('Search error:', error);
        }
      } else {
        setSearchResults([]);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery) {
      navigate(`/shop?search=${searchQuery}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const categories = [
    { name: t('nav.skincare'), slug: 'skincare' },
    { name: t('nav.makeup'), slug: 'makeup' },
    { name: t('nav.perfumes'), slug: 'perfumes' },
    { name: t('nav.jewelry'), slug: 'jewelry' },
    { name: t('nav.clothes'), slug: 'clothes' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'glass shadow-lg' : 'bg-white/80 backdrop-blur-sm'
      }`}>
        {/* Top Bar */}
        <div className="hidden md:block bg-luxury-charcoal text-white text-center py-2 text-sm tracking-wider">
          {t('nav.freeShipping')}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-primary-100 rounded-full transition-colors"
            >
              {isMobileMenuOpen ? (
                <HiOutlineX className="w-6 h-6" />
              ) : (
                <HiOutlineMenu className="w-6 h-6" />
              )}
            </button>

            {/* Logo */}
            <Link 
              to="/" 
              className="flex-shrink-0"
            >
              <h1 className="font-serif text-3xl font-semibold tracking-wider text-luxury-charcoal">
                LUXE
              </h1>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8 relative z-10">
              {categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => handleCategoryClick(category.slug)}
                  className="text-sm uppercase tracking-widest text-gray-700 hover:text-luxury-gold transition-colors duration-300 relative group cursor-pointer"
                >
                  {category.name}
                  <span className="absolute bottom-0 left-0 w-0 h-px bg-luxury-gold transition-all duration-300 group-hover:w-full" />
                </button>
              ))}
            </nav>

            {/* Right Icons */}
            <div className="flex items-center space-x-4 relative z-10">
              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Search */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-2 hover:bg-primary-100 rounded-full transition-colors"
              >
                <HiOutlineSearch className="w-5 h-5" />
              </button>

              {/* Wishlist */}
              <Link
                to="/wishlist"
                className="p-2 hover:bg-primary-100 rounded-full transition-colors relative"
              >
                <HiOutlineHeart className="w-5 h-5" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-luxury-charcoal text-white text-xs flex items-center justify-center rounded-full">
                    {wishlistCount}
                  </span>
                )}
              </Link>

              {/* Cart */}
              <Link
                to="/cart"
                className="p-2 hover:bg-primary-100 rounded-full transition-colors relative"
              >
                <HiOutlineShoppingBag className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-luxury-charcoal text-white text-xs flex items-center justify-center rounded-full">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* User */}
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="p-2 hover:bg-primary-100 rounded-full transition-colors"
                >
                  <HiOutlineUser className="w-5 h-5" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute end-0 mt-2 w-56 bg-white shadow-xl rounded-none border border-primary-200 animate-slide-down">
                    {isAuthenticated ? (
                      <>
                        <div className="px-4 py-3 border-b border-primary-100">
                          <p className="text-sm text-gray-500">{t('auth.welcomeBack')}</p>
                          <p className="font-medium">{user?.firstName}</p>
                        </div>
                        <Link
                          to="/account"
                          className="block px-4 py-3 text-sm hover:bg-primary-50 transition-colors"
                        >
                          {t('nav.myAccount')}
                        </Link>
                        <Link
                          to="/orders"
                          className="block px-4 py-3 text-sm hover:bg-primary-50 transition-colors"
                        >
                          {t('nav.myOrders')}
                        </Link>
                        <Link
                          to="/wishlist"
                          className="block px-4 py-3 text-sm hover:bg-primary-50 transition-colors"
                        >
                          {t('nav.myWishlist')}
                        </Link>
                        <button
                          onClick={logout}
                          className="block w-full text-start px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-primary-100"
                        >
                          {t('common.logout')}
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          className="block px-4 py-3 text-sm hover:bg-primary-50 transition-colors"
                        >
                          {t('auth.signIn')}
                        </Link>
                        <Link
                          to="/register"
                          className="block px-4 py-3 text-sm hover:bg-primary-50 transition-colors border-t border-primary-100"
                        >
                          {t('auth.createAccount')}
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Search Overlay */}
        {isSearchOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t border-primary-100 animate-slide-down">
            <div className="max-w-3xl mx-auto px-4 py-8">
              <form onSubmit={handleSearch}>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for products..."
                    className="w-full text-lg border-b-2 border-primary-200 pb-4 pr-12 focus:outline-none focus:border-luxury-charcoal transition-colors bg-transparent"
                    autoFocus
                  />
                  <button type="submit" className="absolute right-0 top-0 p-2">
                    <HiOutlineSearch className="w-6 h-6 text-gray-400" />
                  </button>
                </div>
              </form>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-6 space-y-4">
                  <p className="text-sm text-gray-500 uppercase tracking-wider">Suggestions</p>
                  {searchResults.slice(0, 5).map((product) => (
                    <Link
                      key={product._id}
                      to={`/product/${product.slug}`}
                      onClick={() => setIsSearchOpen(false)}
                      className="flex items-center space-x-4 p-2 hover:bg-primary-50 transition-colors"
                    >
                      <img
                        src={product.images?.[0]?.url}
                        alt={product.name}
                        className="w-16 h-16 object-cover"
                      />
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-sm text-gray-500">${product.price}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
        isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
        <div className={`absolute left-0 top-0 bottom-0 w-80 bg-white shadow-xl transform transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="p-6">
            <Link to="/" className="block mb-8">
              <h1 className="font-serif text-2xl font-semibold tracking-wider">LUXE</h1>
            </Link>
            <nav className="space-y-4">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/shop?category=${category.slug}`}
                  className="block py-2 text-lg hover:text-luxury-gold transition-colors"
                >
                  {category.name}
                </Link>
              ))}
              <hr className="my-4" />
              {isAuthenticated ? (
                <>
                  <Link to="/account" className="block py-2 text-lg hover:text-luxury-gold transition-colors">
                    My Account
                  </Link>
                  <Link to="/orders" className="block py-2 text-lg hover:text-luxury-gold transition-colors">
                    My Orders
                  </Link>
                  <button
                    onClick={logout}
                    className="block py-2 text-lg text-red-600 hover:text-red-700 transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block py-2 text-lg hover:text-luxury-gold transition-colors">
                    Sign In
                  </Link>
                  <Link to="/register" className="block py-2 text-lg hover:text-luxury-gold transition-colors">
                    Create Account
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </div>

      {/* Spacer for fixed navbar */}
      <div className="h-20 md:h-28" />
    </>
  );
};

export default Navbar;
